import { supabase, isSupabaseConfigured } from "@/lib/supabase";
import {
    getAIPhoneOutcomeFilterValues,
    getAIPhoneStatusFilterValues,
    normalizeAIPhoneInboxRoute,
    normalizeAIPhoneOutcome,
    normalizeAIPhoneStatus,
} from "@/types/ai-phone";
import type { AIPhoneCall, AIPhoneCallFilters, PhoneStats, RecentCall } from "@/types/ai-phone";
import { formatDuration } from "@/lib/utils";
import { startOfDay, subDays, startOfWeek, startOfMonth } from "date-fns";
import type { PhonePeriodFilter } from "@/types/ai-phone";
import { logger } from "@/lib/logger";

let todayStatsSelectMode: "full" | "without_quality" | "minimal" = "without_quality";
let weekStatsSelectMode: "with_outcome" | "minimal" = "with_outcome";

type PhoneStatsOptions = {
    doctorOnly?: boolean;
};

function isMissingColumnError(error: unknown): boolean {
    if (!error || typeof error !== "object") return false;
    const code = String((error as { code?: string }).code || "");
    const message = String((error as { message?: string }).message || "").toLowerCase();
    return code === "42703" || message.includes("column") && message.includes("does not exist");
}

async function fetchTodayStatsRows(
    practiceId: string,
    todayISO: string,
): Promise<Record<string, unknown>[]> {
    for (;;) {
        const { data, error } =
            todayStatsSelectMode === "full"
                ? await supabase
                      .from("ai_phone_calls")
                        .select("id,status,duration_seconds,quality_score,call_outcome,routed_to_inbox,call_started_at")
                      .eq("practice_id", practiceId)
                      .gte("call_started_at", todayISO)
                : todayStatsSelectMode === "without_quality"
                  ? await supabase
                        .from("ai_phone_calls")
                        .select("id,status,duration_seconds,call_outcome,routed_to_inbox,call_started_at")
                        .eq("practice_id", practiceId)
                        .gte("call_started_at", todayISO)
                  : await supabase
                        .from("ai_phone_calls")
                        .select("id,status,duration_seconds,routed_to_inbox,call_started_at")
                        .eq("practice_id", practiceId)
                        .gte("call_started_at", todayISO);

        if (!error) return (data as Record<string, unknown>[]) || [];

        if (isMissingColumnError(error)) {
            if (todayStatsSelectMode === "full") {
                todayStatsSelectMode = "without_quality";
                continue;
            }
            if (todayStatsSelectMode === "without_quality") {
                todayStatsSelectMode = "minimal";
                continue;
            }
        }

        logger.warn("[ai-phone.service] getPhoneStats today query failed:", error);
        return [];
    }
}

async function fetchWeekStatsRows(
    practiceId: string,
    weekISO: string,
): Promise<Record<string, unknown>[]> {
    for (;;) {
        const { data, error } =
            weekStatsSelectMode === "with_outcome"
                ? await supabase
                      .from("ai_phone_calls")
                    .select("id,call_outcome,duration_seconds,routed_to_inbox")
                      .eq("practice_id", practiceId)
                      .gte("call_started_at", weekISO)
                : await supabase
                      .from("ai_phone_calls")
                      .select("id,duration_seconds")
                      .eq("practice_id", practiceId)
                      .gte("call_started_at", weekISO);

        if (!error) return (data as Record<string, unknown>[]) || [];

        if (isMissingColumnError(error) && weekStatsSelectMode === "with_outcome") {
            weekStatsSelectMode = "minimal";
            continue;
        }

        logger.warn("[ai-phone.service] getPhoneStats week query failed:", error);
        return [];
    }
}

/** Map DB row to screen-friendly AIPhoneCall */
function mapAIPhoneCall(call: AIPhoneCall): AIPhoneCall {
    // DB column is 'call_outcome' but app uses 'outcome'
    const raw = call as unknown as Record<string, unknown>;
    const rawOutcome = (call.outcome || raw.call_outcome) as string | undefined;
    const outcome = normalizeAIPhoneOutcome(rawOutcome);
    const status = normalizeAIPhoneStatus(call.status);
    const routedTo = normalizeAIPhoneInboxRoute(
        (call.routed_to_inbox ?? raw.routed_to_inbox) as string | undefined,
        rawOutcome,
    );
    return {
        ...call,
        outcome,
        status,
        routed_to_inbox: routedTo,
        callerName: call.patient_name,
        callerPhone: call.patient_phone,
        startedAt: call.call_started_at,
        duration: call.duration_seconds,
        direction: call.call_direction,
        reason: call.intent_detected,
    };
}

/**
 * Get AI phone calls with filters
 */
export async function getAIPhoneCalls(
    practiceId: string,
    filters: AIPhoneCallFilters = {},
): Promise<AIPhoneCall[]> {
    if (!isSupabaseConfigured()) return [];

    const outcomeFilterValues = filters.outcome ? [...getAIPhoneOutcomeFilterValues(filters.outcome)] : null;
    const statusFilterValues =
        filters.status && filters.status !== "all" ? [...getAIPhoneStatusFilterValues(filters.status)] : null;

    let query = supabase
        .from("ai_phone_calls")
        .select("*")
        .eq("practice_id", practiceId)
        .neq("status", "archived")
        .order("call_started_at", { ascending: false });

    // Period filter
    if (filters.period && filters.period !== "all") {
        const range = getDateRangeForPeriod(filters.period as PhonePeriodFilter);
        query = query.gte("call_started_at", range.dateFrom);
    }

    if (filters.dateFrom) query = query.gte("call_started_at", filters.dateFrom);
    if (filters.dateTo) query = query.lte("call_started_at", filters.dateTo);
    if (outcomeFilterValues?.length === 1) {
        query = query.eq("call_outcome", outcomeFilterValues[0]);
    }
    if (statusFilterValues?.length === 1) {
        query = query.eq("status", statusFilterValues[0]);
    }
    if (filters.routedTo && filters.routedTo !== "all" && filters.routedTo !== "doctor") {
        query = query.eq("routed_to_inbox", filters.routedTo);
    }
    if (filters.patientId) query = query.eq("patient_id", filters.patientId);
    if (filters.hasAppointment === true) query = query.not("appointment_id", "is", null);
    if (filters.hasAppointment === false) query = query.is("appointment_id", null);
    if (filters.limit) query = query.limit(filters.limit);

    // Search filter
    if (filters.search) {
        const sanitizedSearch = filters.search.replace(/[%_\\]/g, '\\$&');
        query = query.or(
            `patient_name.ilike.%${sanitizedSearch}%,transcript.ilike.%${sanitizedSearch}%,transcript_summary.ilike.%${sanitizedSearch}%`,
        );
    }

    const { data, error } = await query;
    if (error) throw new Error(`Anrufe laden fehlgeschlagen: ${error.message}`);

    let calls = ((data as AIPhoneCall[]) || []).map(mapAIPhoneCall);

    if (outcomeFilterValues) {
        const allowedOutcomes = new Set(outcomeFilterValues);
        calls = calls.filter((call) => call.outcome != null && allowedOutcomes.has(call.outcome));
    }

    if (statusFilterValues) {
        const allowedStatuses = new Set(statusFilterValues);
        calls = calls.filter((call) => call.status != null && allowedStatuses.has(call.status));
    }

    if (filters.routedTo === "doctor") {
        calls = calls.filter((call) => call.routed_to_inbox === "doctor" || call.outcome === "forwarded_to_doctor");
    }

    return calls;
}

/**
 * Get a single call by ID
 */
export async function getAIPhoneCallById(
    practiceId: string,
    callId: string,
): Promise<AIPhoneCall | null> {
    if (!isSupabaseConfigured()) return null;

    const { data, error } = await supabase
        .from("ai_phone_calls")
        .select("*")
        .eq("id", callId)
        .eq("practice_id", practiceId)
        .single();

    if (error) return null;
    return mapAIPhoneCall(data as AIPhoneCall);
}

/**
 * Get phone stats for today
 */
function isDoctorRoutedPhoneTask(row: Record<string, unknown>): boolean {
    return row.routed_to_inbox === "doctor" || row.call_outcome === "forwarded_to_doctor";
}

function isGuestBookingReviewTask(row: Record<string, unknown>): boolean {
    const guestAppointmentId = row.guest_appointment_id;
    const outcome = normalizeAIPhoneOutcome((row.call_outcome as string | undefined) ?? null);

    return (
        typeof guestAppointmentId === "string" &&
        guestAppointmentId.trim().length > 0 &&
        (outcome === "booking_created" || outcome === "appointment_booked")
    );
}

export async function getPhoneStats(
    practiceId: string,
    options?: PhoneStatsOptions,
): Promise<PhoneStats> {
    if (!isSupabaseConfigured()) {
        return {
            todayCalls: 0,
            missedCalls: 0,
            aiHandled: 0,
            avgQuality: 0,
            appointmentsBooked: 0,
            callbacksRequested: 0,
            reviewRequired: 0,
            today: 0,
            thisWeek: 0,
            bookingRate: 0,
            avgDuration: 0,
            peakHour: "–",
            aiHandledRate: 0,
        };
    }

    const todayISO = startOfDay(new Date()).toISOString();
    const weekISO = startOfWeek(new Date(), { weekStartsOn: 1 }).toISOString();

    const calls = await fetchTodayStatsRows(practiceId, todayISO);
    const weekCalls = await fetchWeekStatsRows(practiceId, weekISO);
    const doctorOnly = options?.doctorOnly === true;
    const scopedCalls = doctorOnly ? calls.filter(isDoctorRoutedPhoneTask) : calls;
    const scopedWeekCalls = doctorOnly ? weekCalls.filter(isDoctorRoutedPhoneTask) : weekCalls;
    const qualityScores = scopedCalls
        .map((c) => (c as Record<string, unknown>).quality_score as number | null)
        .filter((s): s is number => s !== null && s !== undefined);

    const durations = [...scopedCalls, ...scopedWeekCalls]
        .map((c) => (c as Record<string, unknown>).duration_seconds as number | null)
        .filter((d): d is number => d !== null && d !== undefined);

    const bookingCount = scopedWeekCalls.filter((c) => {
        const outcome = (c as Record<string, unknown>).call_outcome;
        return outcome === "booking_created" || outcome === "appointment_booked";
    }).length;

    return {
        todayCalls: scopedCalls.length,
        missedCalls: scopedCalls.filter((c) => (c as Record<string, unknown>).status === "missed").length,
        aiHandled: scopedCalls.filter((c) => (c as Record<string, unknown>).status === "completed")
            .length,
        avgQuality:
            qualityScores.length > 0
                ? Math.round(qualityScores.reduce((a, b) => a + b, 0) / qualityScores.length)
                : 0,
        appointmentsBooked: bookingCount,
        callbacksRequested: doctorOnly
            ? 0
            : scopedCalls.filter((c) => {
                  const row = c as Record<string, unknown>;
                  return row.call_outcome === "callback_requested";
              }).length,
        reviewRequired: scopedCalls.filter((c) => {
            const row = c as Record<string, unknown>;
            if (doctorOnly) {
                return isDoctorRoutedPhoneTask(row);
            }
            return (
                row.status === "escalated" ||
                row.call_outcome === "forwarded_to_doctor" ||
                isGuestBookingReviewTask(row)
            );
        }).length,
        today: scopedCalls.length,
        thisWeek: scopedWeekCalls.length,
        bookingRate:
            scopedWeekCalls.length > 0
                ? Math.round((bookingCount / scopedWeekCalls.length) * 100)
                : 0,
        avgDuration:
            durations.length > 0
                ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length)
                : 0,
        peakHour: computePeakHour(scopedCalls as unknown as Record<string, unknown>[]),
        aiHandledRate:
            scopedCalls.length > 0
                ? Math.round(
                      (scopedCalls.filter((c) => (c as Record<string, unknown>).status === "completed")
                          .length /
                          scopedCalls.length) *
                          100,
                  )
                : 0,
    };
}

/** Compute peak hour from today's calls */
function computePeakHour(calls: Record<string, unknown>[]): string {
    if (calls.length === 0) return "–";
    const hourCounts: Record<number, number> = {};
    for (const c of calls) {
        const started = c.call_started_at as string;
        if (!started) continue;
        const h = new Date(started).getHours();
        hourCounts[h] = (hourCounts[h] || 0) + 1;
    }
    const peak = Object.entries(hourCounts).sort((a, b) => b[1] - a[1])[0];
    if (!peak) return "–";
    return `${peak[0].padStart(2, "0")}:00`;
}

/**
 * Get recent calls (mapped to slim format)
 */
export async function getRecentCalls(practiceId: string, limit = 5): Promise<RecentCall[]> {
    if (!isSupabaseConfigured()) return [];

    const todayISO = startOfDay(new Date()).toISOString();

    const { data } = await supabase
        .from("ai_phone_calls")
        .select("*")
        .eq("practice_id", practiceId)
        .gte("call_started_at", todayISO)
        .order("call_started_at", { ascending: false })
        .limit(limit);

    return (data || []).map((call): RecentCall => {
        const c = call as AIPhoneCall;
        const raw = c as unknown as Record<string, unknown>;
                const outcome = normalizeAIPhoneOutcome((c.outcome || raw.call_outcome) as string | undefined);
                const status = normalizeAIPhoneStatus(c.status);
        return {
            id: c.id,
            callerName: c.patient_name || "Unbekannt",
            callerPhone: c.patient_phone,
            time: c.call_started_at,
            duration: formatDuration(c.duration_seconds || 0),
            status:
                                status === "missed"
                    ? "missed"
                                        : status === "transferred"
                      ? "transferred"
                                            : status === "escalated"
                        ? "escalated"
                                                : status === "in_progress"
                          ? "in_progress"
                          : "completed",
            summary: c.transcript_summary || c.summary,
            outcome,
            sentiment: c.sentiment,
        };
    });
}

/**
 * Search call transcripts
 */
export async function searchTranscripts(
    practiceId: string,
    searchQuery: string,
    limit = 20,
): Promise<AIPhoneCall[]> {
    if (!isSupabaseConfigured()) return [];

    // Try RPC first, fallback to ILIKE
    try {
        const { data } = await supabase.rpc("search_ai_phone_transcripts", {
            p_practice_id: practiceId,
            p_search_query: searchQuery,
            p_limit: limit,
        });
        if (data) return data as AIPhoneCall[];
    } catch {
        // RPC not available, use fallback
    }

    // Fallback: ILIKE search — sanitize special chars
    const sanitized = searchQuery.replace(/[%_\\]/g, '\\$&');
    const { data } = await supabase
        .from("ai_phone_calls")
        .select("*")
        .eq("practice_id", practiceId)
        .or(
            `transcript.ilike.%${sanitized}%,transcript_summary.ilike.%${sanitized}%,patient_name.ilike.%${sanitized}%`,
        )
        .order("call_started_at", { ascending: false })
        .limit(limit);

    return (data as AIPhoneCall[]) || [];
}

export async function markAIPhoneCallDone(practiceId: string, callId: string): Promise<boolean> {
    if (!isSupabaseConfigured()) return false;

    const { error } = await supabase
        .from("ai_phone_calls")
        .update({
            status: "completed",
            call_outcome: "resolved_by_practice",
            updated_at: new Date().toISOString(),
        })
        .eq("id", callId)
        .eq("practice_id", practiceId);

    return !error;
}

export async function forwardAIPhoneCallToDoctor(
    practiceId: string,
    callId: string,
): Promise<boolean> {
    if (!isSupabaseConfigured()) return false;

    const { error } = await supabase
        .from("ai_phone_calls")
        .update({
            routed_to_inbox: "doctor",
            status: "escalated",
            call_outcome: "forwarded_to_doctor",
            updated_at: new Date().toISOString(),
        })
        .eq("id", callId)
        .eq("practice_id", practiceId);

    return !error;
}

/**
 * Soft-archive an AI phone call (audit records are never deleted)
 */
export async function archiveAIPhoneCall(practiceId: string, callId: string): Promise<boolean> {
    if (!isSupabaseConfigured()) return false;

    const { error } = await supabase
        .from("ai_phone_calls")
        .update({ status: "archived", updated_at: new Date().toISOString() })
        .eq("id", callId)
        .eq("practice_id", practiceId);

    return !error;
}

/**
 * Get date range for period filter
 */
export function getDateRangeForPeriod(period: PhonePeriodFilter): {
    dateFrom: string;
    dateTo: string;
} {
    const now = new Date();
    const dateTo = now.toISOString();

    switch (period) {
        case "today":
            return { dateFrom: startOfDay(now).toISOString(), dateTo };
        case "week":
            return {
                dateFrom: startOfWeek(subDays(now, 0), { weekStartsOn: 1 }).toISOString(),
                dateTo,
            };
        case "month":
            return { dateFrom: startOfMonth(now).toISOString(), dateTo };
    }
}

/**
 * Get pending phone follow-ups (callbacks und review-pflichtige Eskalationen, nicht archiviert).
 * Used by dashboard worklist und Kommunikationsbedarf.
 */
export async function getPendingCallbacks(
    practiceId: string,
    limit = 10,
    options?: { doctorOnly?: boolean },
): Promise<AIPhoneCall[]> {
    if (!isSupabaseConfigured()) return [];

    try {
        const doctorOnly = options?.doctorOnly === true;
        const followUpFilter = doctorOnly
            ? "call_outcome.eq.forwarded_to_doctor,routed_to_inbox.eq.doctor"
            : "call_outcome.eq.callback_requested,call_outcome.eq.forwarded_to_doctor,status.eq.escalated,and(guest_appointment_id.not.is.null,call_outcome.eq.booking_created),and(guest_appointment_id.not.is.null,call_outcome.eq.appointment_booked)";
        let query = supabase
            .from("ai_phone_calls")
            .select("*")
            .eq("practice_id", practiceId)
            .or(followUpFilter)
            .neq("status", "archived");

        if (doctorOnly) {
            query = query.eq("status", "escalated");
        }

        const { data, error } = await query.order("call_started_at", { ascending: false }).limit(limit);

        if (error) {
            logger.warn("[ai-phone.service] getPendingCallbacks failed:", error);
            return [];
        }

        const calls = ((data as AIPhoneCall[]) || []).map(mapAIPhoneCall);

        if (doctorOnly) {
            return calls;
        }

        return calls.filter((call) => {
            if (
                call.outcome === "callback_requested" ||
                call.outcome === "forwarded_to_doctor" ||
                call.status === "escalated"
            ) {
                return true;
            }

            return isGuestBookingReviewTask(call as unknown as Record<string, unknown>);
        });
    } catch (err) {
        logger.error("[ai-phone.service] getPendingCallbacks error:", err);
        return [];
    }
}

// ═══════════════════════════════════════════════════════════════════════════
// AI PHONE SETTINGS (Supabase-backed practice_settings.ai_phone)
// ═══════════════════════════════════════════════════════════════════════════

export interface AIPhoneSettings {
    enabled: boolean;
    greetingMessage: string;
    handoffToHuman: boolean;
    maxCallDurationMinutes: number;
}

export const DEFAULT_AI_PHONE_SETTINGS: AIPhoneSettings = {
    enabled: true,
    greetingMessage: "Willkommen in unserer Praxis. Wie kann ich Ihnen helfen?",
    handoffToHuman: true,
    maxCallDurationMinutes: 5,
};

export async function getAIPhoneSettings(practiceId: string): Promise<AIPhoneSettings> {
    if (!isSupabaseConfigured() || !practiceId) return DEFAULT_AI_PHONE_SETTINGS;

    try {
        const { data, error } = await supabase
            .from("practice_settings")
            .select("ai_phone")
            .eq("practice_id", practiceId)
            .maybeSingle();

        if (error || !data?.ai_phone) return DEFAULT_AI_PHONE_SETTINGS;

        const raw = data.ai_phone as Record<string, unknown>;
        return {
            enabled: typeof raw.enabled === "boolean" ? raw.enabled : DEFAULT_AI_PHONE_SETTINGS.enabled,
            greetingMessage: typeof raw.greetingMessage === "string" ? raw.greetingMessage : DEFAULT_AI_PHONE_SETTINGS.greetingMessage,
            handoffToHuman: typeof raw.handoffToHuman === "boolean" ? raw.handoffToHuman : DEFAULT_AI_PHONE_SETTINGS.handoffToHuman,
            maxCallDurationMinutes: typeof raw.maxCallDurationMinutes === "number" ? raw.maxCallDurationMinutes : DEFAULT_AI_PHONE_SETTINGS.maxCallDurationMinutes,
        };
    } catch {
        return DEFAULT_AI_PHONE_SETTINGS;
    }
}

export async function saveAIPhoneSettings(practiceId: string, settings: AIPhoneSettings): Promise<void> {
    if (!isSupabaseConfigured() || !practiceId) {
        throw new Error("Praxis-Kontext fehlt.");
    }

    const { error } = await supabase.from("practice_settings").upsert(
        {
            practice_id: practiceId,
            ai_phone: settings,
            updated_at: new Date().toISOString(),
        },
        { onConflict: "practice_id" },
    );

    if (error) {
        throw new Error("KI-Telefon-Einstellungen konnten nicht gespeichert werden.");
    }
}
